import React, { useState, useCallback, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddCircleOutline, MoreVert, Visibility, Edit, Delete } from "@mui/icons-material";
import { Search } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { useNavigate } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import {
  getDynamicTemplateApi,
  deleteDynamicTemplateApi,
} from "../services/dynamic-template.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { getItemApi } from "../services/item.service";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import {
  PathologySubpageShell,
  PATHOLOGY_PAGE_TITLE_SX,
} from "../components/Pathology/pathologyPageChrome";
import { toast } from "react-toastify";

const formTypeList = ["Pathology", "Radiology"];

const searchByOptions = [
  { value: "name", label: "Name" },
  { value: "formType", label: "Type" },
  { value: "priority", label: "Priority" },
];

export default function MasterDynamicTemplate() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("name");
  const [item, setItem] = useState(null);
  const [formType, setFormType] = useState("Pathology");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);

  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionRow, setActionRow] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const searchByLabel =
    searchByOptions.find((o) => o.value === searchBy)?.label || "Name";

  const { dataList: itemList } = useGetApi({
    apiFunction: getItemApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "items",
  });

  const itemListWithAll = useMemo(
    () => [{ _id: "", itemName: "All" }, ...(itemList || [])],
    [itemList]
  );

  const {
    dataList: dynamicTemplateList,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getDynamicTemplateApi,
    params: {
      limit: Number.MAX_SAFE_INTEGER,
      search,
      searchBy,
      item: item?._id,
      formType,
    },
    dependencies: [search, searchBy, item, formType],
    debounceDelay: 500,
    dataKey: "templates",
  });

  const rawRows = useMemo(
    () =>
      (dynamicTemplateList || []).map((row, idx) => ({
        id: row._id || row.id || idx,
        _id: row._id,
        name: row?.name || "-",
        formType: row?.formType || "-",
        priority: row?.priority ?? "-",
        status: row?.status ?? "Active",
        _original: row,
      })),
    [dynamicTemplateList]
  );

  const sortField = sortModel[0]?.field || "name";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = useMemo(
    () =>
      [...rawRows].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        const cmp = String(aVal ?? "").localeCompare(String(bVal ?? ""), undefined, {
          numeric: true,
        });
        return sortOrder === "asc" ? cmp : -cmp;
      }),
    [rawRows, sortField, sortOrder]
  );
  const pageSize = paginationModel.pageSize;
  const page = paginationModel.page;
  const rows =
    sortedRows.length > pageSize
      ? sortedRows.slice(page * pageSize, page * pageSize + pageSize)
      : sortedRows;

  const handleAddItem = () => navigate("add");

  const handleOpenActionMenu = useCallback((event, row) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionRow(row);
  }, []);

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionRow(null);
  };

  const handleViewTemplate = () => {
    const id = actionRow?._id;
    handleCloseActionMenu();
    if (id) navigate(`add/${id}?mode=view`);
  };

  const handleEditTemplate = () => {
    const id = actionRow?._id;
    handleCloseActionMenu();
    if (id) navigate(`add/${id}`);
  };

  const handleAskDelete = () => {
    setDeleteConfirmOpen(true);
    setActionMenuAnchor(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setActionRow(null);
  };

  const handleDeleteConfirm = async () => {
    const id = actionRow?._id;
    if (!id) {
      handleDeleteCancel();
      return;
    }
    setDeleting(true);
    try {
      const res = await deleteDynamicTemplateApi(id);
      if (res?.code === 200 && res?.isSuccess !== false) {
        toast.success(res?.data?.message || res?.message || "Template deleted");
        await refetch();
      } else {
        toast.error(res?.message || "Could not delete template");
      }
    } catch (e) {
      toast.error(e?.message || "Could not delete template");
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setActionRow(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "formType",
        headerName: "Type",
        flex: 0.8,
        minWidth: 100,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "priority",
        headerName: "Priority",
        flex: 0.5,
        minWidth: 80,
        headerAlign: "center",
        align: "center",
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        sortable: true,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0,
        width: 64,
        minWidth: 64,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            aria-label="open actions menu"
            onClick={(e) => handleOpenActionMenu(e, params.row)}
          >
            <MoreVert sx={{ fontSize: "20px" }} />
          </IconButton>
        ),
      },
    ],
    [handleOpenActionMenu]
  );

  const dataGridSx = {
    border: "none",
    "& .MuiDataGrid-root": { border: "none", width: "100%" },
    "& .MuiDataGrid-cell": {
      borderBottom: "1px solid #f0f0f0",
      padding: isMobile ? "4px" : "8px",
      fontSize: isMobile ? "12px" : "14px",
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f5f5f5",
      borderBottom: "2px solid #e0e0e0",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#f5f5f5",
      fontWeight: "bold",
    },
    "& .MuiDataGrid-row": {
      "&:hover": { backgroundColor: "#f5f5f5" },
      "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
      "&:nth-of-type(even)": { backgroundColor: "white" },
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "2px solid #e0e0e0",
      backgroundColor: "#f9f9f9",
    },
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", paddingBottom: 2 }}>
          <PathologySubpageShell>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0,
                mb: "20px",
                color: "#5D6A82",
              }}
            >
              <Typography sx={PATHOLOGY_PAGE_TITLE_SX}>
                Dynamic Template Master
              </Typography>
            </Box>
            <Box
              sx={{
                mb: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                paddingBottom: 2,
              }}
            >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <TextField
                placeholder={`Search by ${searchByLabel}`}
                size="small"
                sx={{ width: "300px" }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <select
                          className="uid-select"
                          value={searchBy}
                          onChange={(e) => setSearchBy(e.target.value)}
                          style={{
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: "#333",
                            fontWeight: "500",
                          }}
                        >
                          {searchByOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
                value={search || ""}
                onChange={(e) => setSearch(e.target.value || "")}
              />
              <Autocomplete
                options={itemListWithAll || []}
                getOptionLabel={(option) => option?.itemName || ""}
                renderInput={(params) => (
                  <TextField {...params} label="Item" size="small" />
                )}
                value={item || null}
                onChange={(_, newValue) => setItem(newValue)}
                sx={{ bgcolor: "white", minWidth: "200px" }}
              />
              <Autocomplete
                options={formTypeList || []}
                renderInput={(params) => (
                  <TextField {...params} label="Type" size="small" />
                )}
                value={formType || ""}
                onChange={(_, newValue) => setFormType(newValue)}
                sx={{ bgcolor: "white", minWidth: "200px" }}
              />
            </Box>
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: "150px",
              }}
              onClick={handleAddItem}
            >
              <AddCircleOutline />
              <Typography sx={{ color: "white" }}>New Template</Typography>
            </Button>
          </Box>

          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box
              sx={{
                height: {
                  xs: "calc(100vh - 380px)",
                  sm: "calc(100vh - 400px)",
                  md: "calc(100vh - 420px)",
                },
                width: "100%",
                minHeight: "300px",
                overflow: "hidden",
              }}
            >
              <DataGrid
                rows={rows}
                columns={columns}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                rowCount={sortedRows.length}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                disableRowSelectionOnClick
                sx={dataGridSx}
                components={{
                  NoRowsOverlay: () => (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No templates found
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}
          </PathologySubpageShell>

          <Menu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor)}
            onClose={handleCloseActionMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={handleViewTemplate}>
              <ListItemIcon>
                <Visibility fontSize="small" />
              </ListItemIcon>
              <ListItemText>View</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleEditTemplate}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAskDelete}>
              <ListItemIcon>
                <Delete fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
            </MenuItem>
          </Menu>

          <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This will permanently remove &ldquo;{actionRow?.name || "this template"}&rdquo;.
                This cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleDeleteCancel}
                disabled={deleting}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                disabled={deleting}
                sx={{ textTransform: "none" }}
              >
                {deleting ? "Deleting…" : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Container>
  );
}
